import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  ArrowLeft,
  Truck,
  MapPin,
  Calendar,
  DollarSign,
  User,
  Phone,
  Mail,
  AlertTriangle,
  CheckCircle,
  Loader2,
  UserCheck,
  RefreshCw,
  Clock,
  Building2,
  History,
  Car,
  CreditCard,
  XCircle,
  Eye,
  EyeOff,
  Navigation,
  Satellite,
} from "lucide-react";
import { toast } from "sonner";


const API_URL = process.env.REACT_APP_BACKEND_URL;
const DISPATCH_STALE_MS = 5 * 60 * 1000; // re-fetch CD snapshot only if older than 5 min
const getMapSrc = (lat, lon) =>
  `https://maps.google.com/maps?q=${lat},${lon}&z=13&output=embed`;
const getRouteSrc = (origin, destination) =>
  `https://maps.google.com/maps?saddr=${encodeURIComponent(origin)}&daddr=${encodeURIComponent(destination)}&output=embed`;

const EVENT_STYLES = {
  STATUS_CHANGE: { bg: "bg-blue-600", label: "STATUS CHANGE" },
  PAYMENT: { bg: "bg-green-600", label: "PAYMENT RECEIVED" },
  LISTING: { bg: "bg-purple-600", label: "POSTED TO LOAD BOARDS" },
  DISPATCH: { bg: "bg-orange-600", label: "CARRIER DISPATCHED" },
  SMS: { bg: "bg-yellow-600", label: "SMS SENT" },
  DATE_CHANGE: { bg: "bg-indigo-600", label: "DATE CHANGE" },
  DRIVER_ASSIGNMENT: { bg: "bg-teal-600", label: "DRIVER ASSIGNED" },
  VEHICLE_PICKUP: { bg: "bg-cyan-600", label: "VEHICLE PICKED UP" },
  VEHICLE_DELIVERED: { bg: "bg-emerald-600", label: "VEHICLE DELIVERED" },
};

function HistoryEventCard({ ev }) {
  const style = EVENT_STYLES[ev.type] || { bg: "bg-gray-600", label: ev.type };
  return (
    <div className="rounded-lg border border-border overflow-hidden text-sm">
      <div
        className={`${style.bg} px-3 py-1.5 font-semibold text-white text-xs tracking-wide`}
      >
        {ev.label || style.label}
      </div>
      <div className="bg-card px-3 py-2 space-y-2">
        {(ev.newValue || ev.oldValue) && (
          <div className="grid grid-cols-2 gap-4">
            {ev.newValue != null && (
              <div>
                <p className="text-xs text-muted-foreground">New</p>
                <p className="font-semibold">{ev.newValue}</p>
              </div>
            )}
            {ev.oldValue != null && (
              <div>
                <p className="text-xs text-muted-foreground">Old</p>
                <p className="font-semibold text-muted-foreground">
                  {ev.oldValue || "--/--/--"}
                </p>
              </div>
            )}
          </div>
        )}
        {ev.notes && (
          <p className="text-xs text-muted-foreground">{ev.notes}</p>
        )}
        <div className="flex justify-end">
          <p className="text-xs text-muted-foreground">
            {ev.actor ? `${ev.actor} Â· ` : ""}
           {ev.ts instanceof Date && !isNaN(ev.ts.getTime()) 
            ? ev.ts.toLocaleString() 
            : '-'}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function LoadDetailPage() {
  const { loadId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [assignDialog, setAssignDialog] = useState(false);
  const [carrierId, setCarrierId] = useState("");
  const [activeCarriers, setActiveCarriers] = useState([]);
  const [carriersLoading, setCarriersLoading] = useState(false);
  const [dispatchRefreshing, setDispatchRefreshing] = useState(false);
  const [autoRefreshing, setAutoRefreshing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const carriersLoadedRef = useRef(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [cancelDispatchDialog, setCancelDispatchDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNotes, setCancelNotes] = useState("");
  const [vehicleLocation, setVehicleLocation] = useState(null);
  const [locationLoading, setLocationLoading] = useState(false);
  const [locationError, setLocationError] = useState(null);

  const applyDispatchResult = useCallback((result) => {
    setData((prev) =>
      prev
        ? {
            ...prev,
            load: {
              ...prev.load,
              cd_dispatch_snapshot: result.data,
              cd_dispatch_fetched_at: new Date().toISOString(),
              ...(result.cd_driver_name && { cd_driver_name: result.cd_driver_name }),
              ...(result.cd_driver_id && { cd_driver_id: result.cd_driver_id }),
              ...(result.cd_listing_status && { cd_listing_status: result.cd_listing_status }),
              ...(result.cd_payment_status && { payment_status: result.cd_payment_status }),
              // Apply any status changes made server-side during refresh (e.g. refunded after CD cancel)
              ...(result.load_status && { status: result.load_status }),
              ...(result.refunded_at !== undefined && { refunded_at: result.refunded_at }),
              ...(result.final_payment_at !== undefined && { final_payment_at: result.final_payment_at }),
              ...(result.cd_escalation_stage !== undefined && { cd_escalation_stage: result.cd_escalation_stage }),
            },
          }
        : prev,
    );
  }, []);

  const fetchActiveCarriers = useCallback(async () => {
    if (carriersLoadedRef.current) return;
    carriersLoadedRef.current = true;
    setCarriersLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/carriers/active`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setActiveCarriers(await response.json());
      } else {
        carriersLoadedRef.current = false;
      }
    } catch (error) {
      carriersLoadedRef.current = false;
      console.error("Error fetching carriers:", error);
    } finally {
      setCarriersLoading(false);
    }
  }, [token]);

  const fetchLoadDetail = useCallback(async (signal) => {
    try {
      setDetailsLoading(true);
      
      const response = await fetch(`${API_URL}/api/admin/loads/${loadId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      if (response.ok) {
        const loadData = await response.json();
        setData(loadData);
        handleRefreshDispatch();
        if (loadData?.load?.cd_dispatch_id) {
          const fetchedAt = loadData.load.cd_dispatch_fetched_at;
          const isStale = !fetchedAt || Date.now() - new Date(fetchedAt).getTime() > DISPATCH_STALE_MS;
          if (isStale) {
            setAutoRefreshing(true);
            fetch(
              `${API_URL}/api/admin/loads/${loadData.load.id}/refresh-dispatch`,
              { method: "POST", headers: { Authorization: `Bearer ${token}` } },
            )
              .then((r) => (r.ok ? r.json() : null))
              .then((result) => { if (result?.data) applyDispatchResult(result); })
              .catch(() => {})
              .finally(() => setAutoRefreshing(false));
          }
        }
      }
      setDetailsLoading(false);
    } catch (error) {
      setLoading(false);
      if (error.name !== "AbortError") console.error("Error fetching load:", error);
    } finally {
      setLoading(false);
    }
  }, [loadId, token, applyDispatchResult]);

  useEffect(() => {
    const controller = new AbortController();
    fetchLoadDetail(controller.signal);
    return () => controller.abort();
  }, [fetchLoadDetail]);

  const fetchVehicleLocation = useCallback(async () => {
    if (!loadId) return;
    setLocationLoading(true);
    setLocationError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/loads/${loadId}/vehicle-location`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const json = await res.json();
      if (json.available) {
        setVehicleLocation(json.location);
      } else {
        setVehicleLocation(null);
        if (json.reason === "premium_required") setLocationError("premium");
        else if (json.reason === "api_error") setLocationError(json.detail || "api_error");
      }
    } catch {
      setLocationError("network_error");
    } finally {
      setLocationLoading(false);
    }
  }, [loadId, token]);

  const pollStartRef   = useRef(null);   // when fast polling began
  const intervalRef    = useRef(null);   // current interval handle
  const slowModeRef    = useRef(false);  // true once switched to 1-hr poll

  useEffect(() => {
    const load = data?.load;
    const activeStatuses = ["dispatched", "picked_up", "in_transit"];
    if (!activeStatuses.includes(load?.status)) return;

    // Initial fetch
    fetchVehicleLocation();
    pollStartRef.current  = Date.now();
    slowModeRef.current   = false;

    // Start 30-second fast poll
    intervalRef.current = setInterval(async () => {
      await fetchVehicleLocation();

      // Check if we have real GPS yet (non-mock)
      // vehicleLocation is read via closure — grab from ref below
    }, 30000);

    return () => clearInterval(intervalRef.current);
  }, [data?.load?.cd_dispatch_id, data?.load?.status, fetchVehicleLocation]);  // eslint-disable-line

  // Separate effect: watches vehicleLocation to decide when to switch poll speed
  useEffect(() => {
    if (!intervalRef.current || slowModeRef.current) return;

    const hasRealGPS = vehicleLocation && !vehicleLocation.mock;
    const elapsed    = pollStartRef.current ? Date.now() - pollStartRef.current : 0;
    const fiveMins   = 5 * 60 * 1000;

    if (!hasRealGPS && elapsed >= fiveMins) {
      // 5 minutes elapsed without real GPS — switch to 1-hour polling
      clearInterval(intervalRef.current);
      slowModeRef.current  = true;
      intervalRef.current  = setInterval(fetchVehicleLocation, 60 * 60 * 1000);
    }
  }, [vehicleLocation, fetchVehicleLocation]);

  const handleDispatch = async () => {
    setActionLoading("dispatch");
    try {
      const response = await fetch(
        `${API_URL}/api/admin/loads/${loadId}/dispatch`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (response.ok) {
        toast.success("Dispatch approved");
        fetchLoadDetail();
      } else {
        throw new Error("Failed to dispatch");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };


  const handleRefreshDispatch = async () => {
    if (!data?.load?.cd_dispatch_id) {
      console.log("Skipping refresh: No CD Dispatch ID present yet.");
      return;
    }
    setDispatchRefreshing(true);
    try {
      const response = await fetch(
        `${API_URL}/api/admin/loads/${loadId}/refresh-dispatch`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const result = await response.json();
      if (response.ok) {
        toast.success("Dispatch details refreshed");
        applyDispatchResult(result);
      } else {
        throw new Error(result.detail || "Failed to refresh dispatch");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setDispatchRefreshing(false);
    }
  };

  const handleCancelDispatch = async (withRefund = false) => {
    setActionLoading(withRefund ? "cancel-refund" : "cancel-dispatch");
    try {
      const response = await fetch(
        `${API_URL}/api/admin/loads/${loadId}/cancel-dispatch`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ reason: cancelReason, notes: cancelNotes, refund: withRefund }),
        },
      );
      const result = await response.json();
      if (response.ok) {
        const action = result.carrier_was_assigned ? "Dispatch cancelled" : "Listing removed";
        const refundMsg = result.refund?.refund_id
          ? ` • Refund of $${result.refund.amount?.toFixed(2)} processed.`
          : result.refund?.error
          ? ` • Refund failed: ${result.refund.error}`
          : "";
        const listingMsg = result.new_listing_id
          ? ` Re-posted as listing ${result.new_listing_id}.`
          : "";
        toast.success(`${action}.${listingMsg}${refundMsg}`);
        setCancelDispatchDialog(false);
        setCancelReason("");
        setCancelNotes("");
        // Listing removed (no carrier case) → go back to loads list
        if (!result.carrier_was_assigned) {
          navigate("/admin/loads");
          return;
        }
        // Carrier cancel → reload detail page with fresh data
        const reload = await fetch(`${API_URL}/api/admin/loads/${loadId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (reload.ok) setData(await reload.json());
      } else {
        throw new Error(result.detail || "Failed to cancel dispatch");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handleAssignCarrier = async () => {
    if (!carrierId.trim()) {
      toast.error("Select a carrier");
      return;
    }
    setActionLoading("assign");
    try {
      const response = await fetch(
        `${API_URL}/api/admin/loads/${loadId}/assign-carrier`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ carrier_id: carrierId.trim() }),
        },
      );
      const result = await response.json();
      if (response.ok) {
        toast.success(
          `Carrier assigned. CD Dispatch ID: ${result.dispatch_id}`,
        );
        setAssignDialog(false);
        setCarrierId("");
        fetchLoadDetail();
      } else {
        throw new Error(result.detail || "Failed to assign carrier");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePatchVin = async () => {
    setActionLoading("patch-vin");
    try {
      const response = await fetch(
        `${API_URL}/api/cd/dispatch/patch-vin/${loadId}`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const result = await response.json();
      if (response.ok) {
        toast.success(`VIN patched on CD dispatch (${result.vehicles_updated} vehicle(s) updated)`);
        const reload = await fetch(`${API_URL}/api/admin/loads/${loadId}`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (reload.ok) setData(await reload.json());
      } else {
        throw new Error(result.detail || "Failed to patch VIN");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-accent" />
      </div>
    );
  }

  if (detailsLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-40 gap-4">
        <Loader2 className="h-10 w-10 animate-spin text-accent" />
        <p className="text-muted-foreground animate-pulse">Loading load details...</p>
      </div>
    );
  }

  if (!detailsLoading && !data?.load) {
    console.log("Loading:", loading);
    return (
      <div className="text-center py-20">
        <p className="text-muted-foreground">Load not found</p>
        <Button onClick={() => navigate("/admin/loads")} className="mt-4">
          Back to Loads
        </Button>
      </div>
    );
  }

  const { load, lead, carrier, offers, sms_phases } = data;

  // Deposit amount: $199 open, $299 enclosed (matches backend payments.py logic)
  const depositAmount = load.transport_type === "enclosed" ? 299 : 199;
  const remainingDue = Math.max(0, (load.customer_price || 0) - depositAmount);

  // CD Dispatch snapshot helpers -- field names from actual CD Fulfillment API response
  const cdDispatch = load?.cd_dispatch_snapshot;
  const cdPickupStop = cdDispatch?.stops?.find((s) => s.sequenceId === 1);
  const cdDeliveryStop = cdDispatch?.stops?.find((s) => s.sequenceId === 2);
  const cdVehicle = cdDispatch?.vehicles?.[0];
  // Driver info.
  // CD stores assigned drivers under dispatchDrivers.users[] (array of UUIDs) at the
  // dispatch root level -- NOT inside vehicles[]. The backend refresh-dispatch endpoint
  // resolves the UUID to a name via the Membership API and persists it as
  // load.cd_driver_name / load.cd_driver_id.
  const driverName = load.cd_driver_name;

  // Build history items (most recent first)
  const smsLabels = {
    1: "TRANSPORT PREFERENCE SMS",
    2: "CARRIER TRUST SMS",
    3: "CHECKOUT LINK SMS",
    4: "CARRIER ASSIGNMENT SMS",
    5: "PICKUP CONFIRMATION SMS",
    6: "MID-TRANSIT SMS",
    7: "DELIVERY CONFIRMATION SMS",
    8: "REVIEW REQUEST SMS",
    9: "REFERRAL OFFER SMS",
  };
  const historyItems = [];
  const hiAdd = (ts, type, opts) => {
    if (ts) historyItems.push({ ts: new Date(ts), type, ...opts });
  };
  hiAdd(load.created_at, "STATUS_CHANGE", {
    label: "STATUS CHANGE",
    newValue: "New",
    oldValue: null,
    notes: "Load created from quote submission",
    actor: "System",
  });
  hiAdd(load.deposit_paid_at, "PAYMENT", {
    label: "DEPOSIT RECEIVED",
    newValue: `$${depositAmount.toFixed(2)}`,
    oldValue: null,
    notes: `Customer deposit payment confirmed. Balance due at pickup: $${remainingDue.toFixed(2)}`,
    actor: "System",
  });
  


  hiAdd(load.posted_at, "LISTING", {
    label: "POSTED TO LOAD BOARDS",
    newValue: "Posted",
    oldValue: "Deposit Paid",
    notes: load.central_dispatch_listing_id
      ? `Listing ID: ${load.central_dispatch_listing_id}`
      : "Load posted to Central Dispatch",
    actor: "System",
  });
  hiAdd(load.cd_dispatch_fetched_at, "DISPATCH", {
    label: "CARRIER DISPATCHED",
    newValue: "Dispatched",
    oldValue: "Posted",
    notes: load.cd_dispatch_id
      ? `CD Dispatch ID: ${load.cd_dispatch_id}`
      : null,
    actor: "System",
  });
  if (load.cd_driver_id) {
    hiAdd(load.cd_driver_id, "DRIVER_ASSIGNMENT", {
    label: "DRIVER ASSIGNED",
    newValue: `${load.cd_driver_name ? `Name: ${load.cd_driver_name}` : `ID: ${load.cd_driver_id || '-'}`}`,
    oldValue: null,
    notes: `Driver has been assigned to the load.`,
    actor: "System",
  });
  }
  if(load.picked_up_at) {
    hiAdd(load.picked_up_at, "VEHICLE_PICKUP", {
      label: "PICKED UP",
      newValue: null,
      oldValue: null,
      notes: `Load picked up at ${new Date(load.picked_up_at).toLocaleString()}`,
      actor: "System",
    });
  }
  if(load.delivered_at) {
    hiAdd(load.delivered_at, "VEHICLE_DELIVERED", {  
      label: "DELIVERED", 
      newValue: null,
      oldValue: null, 
      notes: `Load delivered at ${new Date(load.delivered_at).toLocaleString()}`,
      actor: "System",    
   });
  }

  (sms_phases || []).forEach((p) => {
    hiAdd(p.sent_at, "SMS", {
      label: smsLabels[p.phase] || `SMS PHASE ${p.phase}`,
      newValue: null,
      oldValue: null,
      notes: p.customer_response
        ? `Customer replied: "${p.customer_response}"`
        : "Message delivered",
      actor: "System",
    });
  });
  //historyItems.sort((a, b) => b.ts - a.ts);

  // Build complete timeline (chronological, for sidebar)
  const tlEvents = [];
  const tlAdd = (ts, label, detail, color) => {
    if (ts)
      tlEvents.push({ ts: new Date(ts), label, detail: detail || null, color });
  };
  tlAdd(load.created_at, "Quote Submitted", null, "#3b82f6");
  tlAdd(load.deposit_paid_at, "Deposit Paid", ``, "#22c55e");
  tlAdd(
    load.posted_at,
    "Posted to Load Boards",
    load.central_dispatch_listing_id
      ? `Listing: ${load.central_dispatch_listing_id}`
      : null,
    "#a855f7",
  );
  tlAdd(
    load.cd_dispatch_fetched_at,
    "CD Dispatch Data Fetched",
    load.cd_dispatch_id || null,
    "#f97316",
  );
  (sms_phases || []).forEach((p) =>
    tlAdd(
      p.sent_at,
      smsLabels[p.phase]?.replace(" SMS", "") || `SMS Phase ${p.phase}`,
      p.customer_response ? `Customer: ${p.customer_response}` : null,
      "#eab308",
    ),
  );
  tlEvents.sort((a, b) => a.ts - b.ts);

  const statusColors = {
    new: "bg-blue-500/10 text-blue-500",
    quoted: "bg-yellow-500/10 text-yellow-500",
    deposit_paid: "bg-green-500/10 text-green-500",
    posted: "bg-purple-500/10 text-purple-500",
    dispatched: "bg-orange-500/10 text-orange-500",
    picked_up: "bg-indigo-500/10 text-indigo-500",
    delivered: "bg-emerald-500/10 text-emerald-500",
    closed: "bg-gray-500/10 text-gray-500",
    refunded: "bg-red-500/10 text-red-500",
    cancelled: "bg-red-500/10 text-red-400",
  };

  // payment_status -- computed from Stripe deposit fields in get_load_detail
  const paymentStatus = load.payment_status || null;

  const cdDispatchStatusColors = {
    NEW: "bg-teal-500/10 text-teal-400 border-teal-500/20",
    NOT_SIGNED: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    PENDING_PICKUP: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    PENDING_DELIVERY: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    DELIVERED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    PAID: "bg-green-500/10 text-green-400 border-green-500/20",
    REJECTED: "bg-red-500/10 text-red-400 border-red-500/20",
    CANCELED: "bg-red-500/10 text-red-400 border-red-500/20",
    ARCHIVED: "bg-gray-500/10 text-gray-400 border-gray-500/20",
  };

  const cdVehicleStatusColors = {
    PENDING_PICKUP: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    PICKED_UP: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    IN_TRANSIT: "bg-orange-500/10 text-orange-400 border-orange-500/20",
    DELIVERED: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  };

  const cdPaymentStatusColors = {
    UNPAID: "bg-gray-500/10 text-gray-400 border-gray-500/20",
    PARTIALLY_PAID: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
    FULLY_PAID: "bg-green-500/10 text-green-400 border-green-500/20",
    REFUNDED: "bg-red-500/10 text-red-400 border-red-500/20",
  };

  return (
    <>
      <div className="space-y-3" data-testid="load-detail">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => navigate("/admin/loads")}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-outfit text-xl font-semibold leading-tight">
                  {load.year} {load.vehicle_make} {load.vehicle_model}
                </h1>
                {load.central_dispatch_listing_id && (
                  <Badge className="text-[10px] font-mono bg-blue-500/10 text-blue-400 border-blue-500/20">
                    CD #{load.central_dispatch_listing_id}
                  </Badge>
                )}
              </div>
              <p
                className="text-muted-foreground text-xs font-mono mt-0.5 cursor-pointer hover:text-white/60 transition-colors select-all"
                title="Click to select Load ID"
              >
                {load.id}
              </p>
            </div>
          </div>
          <div />
        </div>

        {/* Overview: Status + Timestamps + Actions */}
        <div className="flex gap-3 items-start">
          {/* Left: status cards stacked above timestamps */}
          <div className="flex-1 space-y-2">
            {/* CD Status Cards */}
            <div className="grid grid-cols-3 gap-2">
                {[
                  {
                    label: "Dispatch Status",
                    value: cdDispatch?.status,
                    colors: cdDispatchStatusColors,
                    Icon: Truck,
                  },
                  {
                    label: "Vehicle Status",
                    value: cdVehicle?.status,
                    colors: cdVehicleStatusColors,
                    Icon: Car,
                  },
                  {
                    label: "Payment Status",
                    value: paymentStatus,
                    colors: cdPaymentStatusColors,
                    Icon: CreditCard,
                  },
                ].map(({ label, value, colors, Icon }) => (
                  <div
                    key={label}
                    className="bg-card border border-white/5 rounded-lg p-2.5 flex items-center gap-2.5"
                  >
                    <div className="w-5 h-5 rounded-md bg-muted/30 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-3 w-3 text-muted-foreground" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs text-muted-foreground font-medium leading-none mb-1">
                        {label}
                      </p>
                      {value ? (
                        <Badge
                          className={`${colors[value] ?? "bg-gray-500/10 text-gray-400 border-gray-500/20"} text-xs font-semibold`}
                        >
                          {value.replace(/_/g, " ")}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">--</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

            {/* Timestamps */}
            <div className="grid grid-cols-4 gap-2">
              {[
                {
                  label: "Quote Created",
                  value: load.created_at,
                  Icon: Calendar,
                },
                {
                  label: "Deposit Paid",
                  value: load.deposit_paid_at,
                  Icon: DollarSign,
                },
                { label: "Posted to CD", value: load.posted_at, Icon: Truck },
                { label: "Last Updated", value: load.updated_at, Icon: Clock },
              ].map(({ label, value, Icon }) => (
                <div
                  key={label}
                  className="bg-card border border-white/5 rounded-lg p-2 flex items-start gap-2"
                >
                  <div className="w-5 h-5 rounded-md bg-muted/30 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon className="h-2.5 w-2.5 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground truncate leading-none mb-0.5">
                      {label}
                    </p>
                    {value ? (
                      <>
                        <p className="text-xs font-medium leading-snug">
                          {new Date(value).toLocaleDateString("en-US", {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(value).toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                          })}
                        </p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">--</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Actions Card */}
          <Card className="bg-card border-white/5 shrink-0 w-52">
            <CardHeader className="py-2 px-3 border-b border-white/5">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Actions
                </p>
                <Badge
                  className={`${
                    ["CANCELLED", "CANCELED", "REJECTED"].includes(cdDispatch?.status) && load.status === "dispatched"
                      ? statusColors["cancelled"]
                      : statusColors[load.status]
                  } font-semibold text-xs`}
                  data-testid="load-status"
                >
                  {["CANCELLED", "CANCELED", "REJECTED"].includes(cdDispatch?.status) && load.status === "dispatched"
                    ? "cancelled"
                    : load.status?.replace(/_/g, " ")}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-1.5 p-2">
              {["CANCELLED", "CANCELED", "REJECTED"].includes(cdDispatch?.status) && load.status === "dispatched" && (
                <div className="bg-orange-500/10 border border-orange-500/20 rounded-md px-2.5 py-2 flex items-start gap-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-orange-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-orange-400 leading-none">Cancelled by CD</p>
                    <p className="text-[10px] text-orange-400/70 mt-0.5">Click "Refresh CD Data" to sync status.</p>
                  </div>
                </div>
              )}
              {load.refunded_at && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-md px-2.5 py-2 flex items-start gap-2">
                  <XCircle className="h-3.5 w-3.5 text-red-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-red-400 leading-none">Deposit Refunded</p>
                    <p className="text-[10px] text-red-400/70 mt-0.5">
                      {new Date(load.refunded_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                    </p>
                  </div>
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => setHistoryOpen(true)}
              >
                <History className="h-4 w-4 mr-2" />
                History
              </Button>
              <Separator />
              {load.status === "deposit_paid" && !load.refunded_at && (
                <Button
                  className="w-full bg-accent text-accent-foreground"
                  onClick={handleDispatch}
                  disabled={actionLoading === "dispatch"}
                  data-testid="dispatch-btn"
                >
                  {actionLoading === "dispatch" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve Dispatch
                    </>
                  )}
                </Button>
              )}
              {["deposit_paid", "posted", "dispatched"].includes(load.status) &&
                !load.selected_carrier_id &&
                load.central_dispatch_listing_id && (
                  <Button
                    className="w-full justify-start"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setAssignDialog(true);
                      fetchActiveCarriers();
                    }}
                    disabled={actionLoading === "assign"}
                    data-testid="assign-carrier-btn"
                  >
                    {actionLoading === "assign" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <UserCheck className="h-4 w-4 mr-2" />
                        Assign Carrier
                      </>
                    )}
                  </Button>
                )}
              {load.cd_dispatch_id && (
                <Button
                  className="w-full justify-start"
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshDispatch}
                  disabled={dispatchRefreshing}
                  data-testid="refresh-dispatch-btn"
                >
                  {dispatchRefreshing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Refresh CD Data
                    </>
                  )}
                </Button>
              )}
              {/* Cancel Dispatch — carrier is assigned and CD hasn't already cancelled */}
              {load.status === "dispatched" && !["CANCELLED", "CANCELED", "REJECTED"].includes(cdDispatch?.status) && (
                <>
                  <Separator />
                  <Button
                    className="w-full justify-start text-red-500 border-red-500/30 hover:bg-red-500/10 hover:text-red-400"
                    variant="outline"
                    size="sm"
                    onClick={() => setCancelDispatchDialog(true)}
                    disabled={actionLoading === "cancel-dispatch"}
                    data-testid="cancel-dispatch-btn"
                  >
                    {actionLoading === "cancel-dispatch" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 mr-2" />
                        Cancel Dispatch
                      </>
                    )}
                  </Button>
                </>
              )}
              {/* Remove from CD — listing exists but no carrier assigned */}
              {load.status === "posted" && load.central_dispatch_listing_id && !load.cd_dispatch_id && (
                <>
                  <Separator />
                  <Button
                    className="w-full justify-start text-orange-500 border-orange-500/30 hover:bg-orange-500/10 hover:text-orange-400"
                    variant="outline"
                    size="sm"
                    onClick={() => setCancelDispatchDialog(true)}
                    disabled={actionLoading === "cancel-dispatch"}
                    data-testid="remove-listing-btn"
                  >
                    {actionLoading === "cancel-dispatch" ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <XCircle className="h-4 w-4 mr-2" />
                        Remove from CD
                      </>
                    )}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-3">
          {/* Transport Details */}
          <Card className="bg-card border-white/5">
            <CardHeader className="py-2 px-4 border-b border-white/5">
              <CardTitle className="font-outfit text-sm font-semibold">
                Transport Details
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              <div className="grid grid-cols-4 gap-x-6 gap-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <Truck className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Vehicle</p>
                    <p className="font-medium text-sm">
                      {load.year} {load.vehicle_make} {load.vehicle_model}
                    </p>
                    <p className="text-xs text-muted-foreground capitalize">
                      {load.condition} · {load.transport_type}
                    </p>
                    {load.vin && (() => {
                      const vinRevealed = load.cd_escalation_stage === 99 ||
                        ["dispatched", "picked_up", "in_transit", "delivered", "closed"].includes(load.status);
                      const displayVin = vinRevealed
                        ? load.vin
                        : load.vin.slice(0, -4).replace(/./g, '*') + load.vin.slice(-4);
                      return (
                        <div className="mt-1.5">
                          <p className="font-mono text-xs tracking-widest text-foreground">
                            {displayVin}
                          </p>
                          {load.central_dispatch_listing_id && (
                            vinRevealed ? (
                              <span className="inline-flex items-center gap-1 text-[10px] text-emerald-500 mt-0.5">
                                <Eye className="h-3 w-3" /> Revealed on CD
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-[10px] text-amber-500 mt-0.5">
                                <EyeOff className="h-3 w-3" /> Hidden on CD until dispatch
                              </span>
                            )
                          )}
                        </div>
                      );
                    })()}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Route</p>
                    <p className="font-medium text-sm">
                      {load.pickup_zip} → {load.delivery_zip}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {load.distance_miles?.toFixed(0)} miles
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Calendar className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Pickup Window</p>
                    <p className="font-medium text-sm">
                      {new Date(load.pickup_window_start).toLocaleDateString()}{" "}
                      – {new Date(load.pickup_window_end).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <DollarSign className="h-4 w-4 text-accent mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-muted-foreground">Pricing</p>
                    <p className="font-medium text-sm">
                      ${load.customer_price?.toFixed(2)} total
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Deposit: ${depositAmount.toFixed(2)} · Due at pickup: ${remainingDue.toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>

              {load.notes && (
                <div className="pt-3 mt-3 border-t border-white/5">
                  <p className="text-xs text-muted-foreground mb-0.5">Notes</p>
                  <p className="text-sm">{load.notes}</p>
                </div>
              )}
            </CardContent>
          </Card>
          {/* Live Tracking Map — always visible when pickup/delivery ZIPs are known */}
          {load.pickup_zip && load.delivery_zip && (
            <Card className="bg-card border-white/5">
              <CardHeader className="pb-3">
                <CardTitle className="font-outfit text-lg flex items-center gap-2">
                  <Satellite className="h-4 w-4 text-accent" />
                  {vehicleLocation?.mock ? "Simulated Location" : vehicleLocation ? "Live Vehicle Location" : "Route Map"}
                  {locationLoading && (
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground ml-1" />
                  )}
                  {vehicleLocation?.mock ? (
                    <span className="ml-2 flex items-center gap-1 text-[10px] text-yellow-400 font-normal">
                      <span className="h-1.5 w-1.5 rounded-full bg-yellow-400" />
                      Simulated
                    </span>
                  ) : vehicleLocation ? (
                    <span className="ml-2 flex items-center gap-1 text-[10px] text-emerald-400 font-normal">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      Live GPS
                    </span>
                  ) : (
                    <span className="ml-2 text-[10px] text-muted-foreground font-normal">
                      {load.pickup_zip} → {load.delivery_zip}
                    </span>
                  )}
                  <button
                    onClick={fetchVehicleLocation}
                    className="ml-auto text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 font-normal"
                  >
                    <RefreshCw className="h-3 w-3" /> Refresh
                  </button>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0 pb-0">
                {(() => {
                  const lat = vehicleLocation?.latitude ?? vehicleLocation?.lat;
                  const lon = vehicleLocation?.longitude ?? vehicleLocation?.lon ?? vehicleLocation?.lng;
                  const hasGPS = lat && lon;

                  const origin = load.pickup_city
                    ? `${load.pickup_city}, ${load.pickup_state} ${load.pickup_zip}`
                    : load.pickup_zip;
                  const destination = load.delivery_city
                    ? `${load.delivery_city}, ${load.delivery_state} ${load.delivery_zip}`
                    : load.delivery_zip;

                  const mapSrc = hasGPS ? getMapSrc(lat, lon) : getRouteSrc(origin, destination);
                  const updatedAt = vehicleLocation?.locationDateUtc ?? vehicleLocation?.timestamp;
                  const eventType = vehicleLocation?.eventType;

                  return (
                    <div>
                      <iframe
                        key={mapSrc}
                        title="Route Map"
                        src={mapSrc}
                        className="w-full"
                        style={{ height: 320, border: "none", display: "block" }}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                        allowFullScreen
                      />
                      <div className="px-4 py-3 flex items-center justify-between border-t border-white/5 text-xs text-muted-foreground">
                        <div className="flex items-center gap-3">
                          {hasGPS ? (
                            <>
                              <span className="font-mono">{lat.toFixed(5)}, {lon.toFixed(5)}</span>
                              {vehicleLocation?.mock ? (
                                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-yellow-500/10 text-yellow-400">
                                  Simulated · 40% en route
                                </span>
                              ) : eventType && (
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide ${
                                  eventType === "IN_TRANSIT"  ? "bg-blue-500/20 text-blue-400" :
                                  eventType === "PICKED_UP"   ? "bg-orange-500/20 text-orange-400" :
                                  eventType === "DELIVERED"   ? "bg-green-500/20 text-green-400" :
                                  "bg-muted text-muted-foreground"
                                }`}>{eventType.replace(/_/g, " ")}</span>
                              )}
                            </>
                          ) : (
                            <span>{origin} → {destination}</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3">
                          {updatedAt && <span>Updated {new Date(updatedAt).toLocaleTimeString()}</span>}
                          {locationError === "premium" && (
                            <span className="text-amber-500">Premium Plan required for GPS</span>
                          )}
                          <a
                            href={hasGPS
                              ? `https://www.google.com/maps?q=${lat},${lon}`
                              : `https://www.google.com/maps/dir/${encodeURIComponent(origin)}/${encodeURIComponent(destination)}`
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent hover:underline flex items-center gap-1"
                          >
                            <Navigation className="h-3 w-3" /> Open in Maps
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>
          )}

          {/* Risk Flags Alert */}
          {load.risk_flags?.length > 0 && (
            <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg px-3 py-2 flex items-center gap-2.5">
              <AlertTriangle className="h-4 w-4 text-yellow-500 flex-shrink-0" />
              <div className="flex flex-wrap gap-x-4 gap-y-0.5">
                {load.risk_flags.map((flag, i) => (
                  <span key={i} className="text-xs text-yellow-400">• {flag.replace(/_/g, " ")}</span>
                ))}
              </div>
            </div>
          )}
          {/* Customer + Carrier Info */}
          <div className="grid grid-cols-2 gap-3">
            {lead && (
              <Card className="bg-card border-white/5">
                <CardHeader className="py-2 px-4 border-b border-white/5">
                  <CardTitle className="font-outfit text-xs font-semibold flex items-center gap-1.5">
                    <User className="h-3 w-3 text-accent" />
                    Customer (Shipper)
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 py-2.5 flex items-center gap-6 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Name</p>
                    <p className="font-medium text-sm">{lead.name}</p>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Phone className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    <span className="text-sm">{lead.phone}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                    <span className="text-xs">{lead.email}</span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Carrier Info (from CD snapshot or assigned carrier) */}
            {(load.selected_carrier_id || cdDispatch) && (
              <Card className="bg-card border-white/5">
                <CardHeader className="py-2 px-4 border-b border-white/5">
                  <CardTitle className="font-outfit text-xs font-semibold flex items-center gap-1.5">
                    <Building2 className="h-3 w-3 text-accent" />
                    Carrier Info
                  </CardTitle>
                </CardHeader>
                <CardContent className="px-4 py-2.5 flex flex-wrap items-center gap-x-6 gap-y-1.5 text-sm">
                  {load.selected_channel && (
                    <div>
                      <p className="text-xs text-muted-foreground">Channel</p>
                      <p className="capitalize">{load.selected_channel}</p>
                    </div>
                  )}
                  {(load.cd_driver_name || load.cd_driver_id) && (
                    <div className="flex items-center gap-2">
                      {/* <UserCheck className="h-3 w-3 text-muted-foreground" /> */}
                      <div>
                        <p className="text-xs text-muted-foreground">Driver</p>
                        <p className="capitalize">
                          {driverName || (
                            <span className="capitalize">
                              {"Kartik Kakarala"}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                  )}
                  {(carrier?.company_name || load.selected_carrier_id) && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Carrier
                      </p>
                      <p className="font-medium">
                        {carrier?.company_name ?? load.selected_carrier_id}
                      </p>
                    </div>
                  )}

                  {load.cd_dispatch_id && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        CD Dispatch ID
                      </p>
                      <p className="font-mono text-xs break-all">
                        {load.cd_dispatch_id}
                      </p>
                    </div>
                  )}
                  {/* {cdDispatch.dispatchSource && (
                        <div>
                          <p className="text-xs text-muted-foreground">Dispatch Source</p>
                          <p className="capitalize">{cdDispatch.dispatchSource.toLowerCase().replace(/_/g, ' ')}</p>
                        </div>
                      )} */}
                  {cdDispatch.balancePaymentMethod && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Balance Payment
                      </p>
                      <p className="capitalize">
                        {cdDispatch.balancePaymentMethod
                          .toLowerCase()
                          .replace(/_/g, " ")}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
          </div>

          {/* â"€â"€ Central Dispatch Details â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
          {cdDispatch && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="font-outfit text-sm font-semibold flex items-center gap-1.5">
                  <Truck className="h-3.5 w-3.5 text-accent" />
                  Central Dispatch Details
                </h2>
                <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  {autoRefreshing && (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  )}
                  {load.cd_dispatch_fetched_at
                    ? `Fetched ${new Date(load.cd_dispatch_fetched_at).toLocaleString()}`
                    : autoRefreshing
                      ? "Refreshing..."
                      : null}
                </span>
              </div>

              {/* Overview */}
              <Card className="bg-card border-white/5">
                <CardHeader className="py-2 px-4 border-b border-white/5">
                  <CardTitle className="text-xs font-semibold">Overview</CardTitle>
                </CardHeader>
                <CardContent className="grid grid-cols-4 gap-3 px-4 py-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Dispatch ID</p>
                    <p className="font-mono text-xs break-all">
                      {cdDispatch.dispatchId || load.cd_dispatch_id}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Status</p>
                    <Badge variant="outline" className="mt-1">
                      {cdDispatch.status || "--"}
                    </Badge>
                  </div>
                  {(cdDispatch.listingId ||
                    load.central_dispatch_listing_id) && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        CD Listing ID
                      </p>
                      <p className="font-mono text-xs break-all">
                        {cdDispatch.listingId ||
                          load.central_dispatch_listing_id}
                      </p>
                    </div>
                  )}
                  {cdDispatch.loadNumber && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Load Number
                      </p>
                      <p className="font-mono">{cdDispatch.loadNumber}</p>
                    </div>
                  )}
                  {/* {cdDispatch.marketplaceId && (
                      <div>
                        <p className="text-xs text-muted-foreground">Marketplace ID</p>
                        <p>{cdDispatch.marketplaceId}</p>
                      </div>
                    )} */}
                  {cdDispatch.externalId && (
                    <div>
                      <p className="text-xs text-muted-foreground">
                        External Order
                      </p>
                      <p className="font-mono text-xs">
                        {cdDispatch.externalId}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Carrier + Vehicle */}
              {/* <div className="grid grid-cols-2 gap-4">
                  <Card className="bg-card border-white/5">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-accent" />
                        Carrier
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2 text-sm">
                      <div>
                        <p className="text-xs text-muted-foreground">Carrier ID (CD)</p>
                        <p className="font-mono text-xs break-all">{cdDispatch.carrierId || load.selected_carrier_id}</p>
                      </div>
                      {(driverName || driverId) && (
                        <div>
                          <p className="text-xs text-muted-foreground">Driver</p>
                          {driverName
                            ? <p className="font-medium">{driverName}</p>
                            : <p className="font-mono text-xs text-muted-foreground">{driverId}</p>
                          }
                        </div>
                      )}
                      {cdDispatch.dispatchSource && (
                        <div>
                          <p className="text-xs text-muted-foreground">Dispatch Source</p>
                          <p className="capitalize">{cdDispatch.dispatchSource.toLowerCase().replace(/_/g, ' ')}</p>
                        </div>
                      )}
                      {cdDispatch.balancePaymentMethod && (
                        <div>
                          <p className="text-xs text-muted-foreground">Balance Payment</p>
                          <p className="capitalize">{cdDispatch.balancePaymentMethod.toLowerCase().replace(/_/g, ' ')}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {cdVehicle && (
                    <Card className="bg-card border-white/5">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Truck className="h-4 w-4 text-accent" />
                          Vehicle on Dispatch
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-2 text-sm">
                        {cdVehicleDetails && (
                          <p className="font-medium">
                            {cdVehicleDetails.year} {cdVehicleDetails.make} {cdVehicleDetails.model}
                          </p>
                        )}
                        <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                          {cdVehicleDetails?.vehicleType && (
                            <div>
                              <p className="text-muted-foreground">Type</p>
                              <p>{cdVehicleDetails.vehicleType}</p>
                            </div>
                          )}
                          <div>
                            <p className="text-muted-foreground">Operable</p>
                            <p>{cdVehicleDetails?.isInOperable ? 'No' : 'Yes'}</p>
                          </div>
                          {(cdVehicleDetails?.vin || cdVehicle?.vin) && (
                            <div className="col-span-2">
                              <p className="text-muted-foreground">VIN</p>
                              <p className="font-mono">{cdVehicleDetails?.vin || cdVehicle?.vin}</p>
                            </div>
                          )}
                          {(cdVehicleDetails?.color || cdVehicle?.color) && (
                            <div>
                              <p className="text-muted-foreground">Color</p>
                              <p>{cdVehicleDetails?.color || cdVehicle?.color}</p>
                            </div>
                          )}
                          {(cdVehicleDetails?.lotNumber || cdVehicle?.lotNumber) && (
                            <div>
                              <p className="text-muted-foreground">Lot #</p>
                              <p>{cdVehicleDetails?.lotNumber || cdVehicle?.lotNumber}</p>
                            </div>
                          )}
                        </div>
                        {cdVehicle.status && (
                          <div>
                            <p className="text-xs text-muted-foreground">Vehicle Status</p>
                            <Badge variant="outline" className="text-xs capitalize mt-0.5">
                              {cdVehicle.status.replace(/_/g, ' ').toLowerCase()}
                            </Badge>
                          </div>
                        )}
                        {cdVehicle.dispatchVehicleId && (
                          <div>
                            <p className="text-xs text-muted-foreground">Dispatch Vehicle ID</p>
                            <p className="font-mono text-xs break-all">{cdVehicle.dispatchVehicleId}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div> */}

              {/* Route / Stops -- with contact info */}
              {(cdPickupStop || cdDeliveryStop) && (
                <Card className="bg-card border-white/5">
                  <CardHeader className="py-2 px-4 border-b border-white/5">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <MapPin className="h-3 w-3 text-accent" />
                      Route &amp; Stops
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="grid grid-cols-2 gap-4 px-4 py-3 text-sm">
                    {[cdPickupStop, cdDeliveryStop]
                      .filter(Boolean)
                      .map((stop, idx) => (
                        <div key={idx} className="space-y-1.5">
                          <p className="text-xs font-semibold uppercase tracking-wide text-accent">
                            {idx === 0 ? "Pickup" : "Delivery"} -- Stop{" "}
                            {stop.sequenceId}
                          </p>
                          {stop.stopType && (
                            <p className="text-xs text-muted-foreground capitalize">
                              {stop.stopType.toLowerCase().replace(/_/g, " ")}
                            </p>
                          )}
                          {stop.locationName && (
                            <p className="font-medium text-xs">
                              {stop.locationName}
                            </p>
                          )}
                          <div className="space-y-0.5 text-xs">
                            {stop.address &&
                              typeof stop.address === "string" && (
                                <p>{stop.address}</p>
                              )}
                            <p>
                              {[
                                stop.city,
                                stop.state,
                                stop.zipCode || stop.postalCode,
                              ]
                                .filter(Boolean)
                                .join(", ")}
                            </p>
                          </div>
                          {/* Contact info from stop */}
                          {(stop.contactName || stop.name) && (
                            <div className="pt-1 border-t border-white/5 space-y-0.5">
                              <p className="text-xs text-muted-foreground">
                                Contact
                              </p>
                              <div className="flex items-center gap-1.5 text-xs">
                                <User className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                <span className="font-medium">
                                  {stop.contactName || stop.name}
                                </span>
                              </div>
                              {(stop.phone || stop.contactPhone) && (
                                <div className="flex items-center gap-1.5 text-xs">
                                  <Phone className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span>{stop.phone || stop.contactPhone}</span>
                                </div>
                              )}
                              {stop.email && (
                                <div className="flex items-center gap-1.5 text-xs">
                                  <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                                  <span className="break-all">
                                    {stop.email}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                  </CardContent>
                </Card>
              )}

              {/* Pricing + Dates */}
              <div className="grid grid-cols-2 gap-3">
                <Card className="bg-card border-white/5">
                  <CardHeader className="py-2 px-4 border-b border-white/5">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <DollarSign className="h-3 w-3 text-accent" />
                      Pricing
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 px-4 py-3 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">
                        Carrier Price
                      </p>
                      <p className="text-lg font-semibold">
                        $
                        {(
                          cdDispatch.priceTotal ??
                          cdVehicle?.carrierPrice ??
                          0
                        ).toFixed(2)}
                      </p>
                    </div>
                    {/* Payment Terms */}
                    {(cdDispatch.codPaymentMethod ||
                      cdDispatch.balancePaymentMethod) && (
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Payment Terms
                        </p>
                        <p className="text-xs font-medium">
                          Carrier will receive{" "}
                          <span className="font-bold">
                            $
                            {(
                              cdDispatch.codAmount ??
                              cdDispatch.balanceAmount ??
                              cdDispatch.priceTotal ??
                              cdVehicle?.carrierPrice ??
                              0
                            ).toFixed(2)}
                          </span>{" "}
                          via{" "}
                          <span className="capitalize">
                            {(
                              cdDispatch.codPaymentMethod ||
                              cdDispatch.balancePaymentMethod ||
                              ""
                            )
                              .replace(/_/g, " ")
                              .toLowerCase()}
                          </span>{" "}
                          upon{" "}
                          <span className="capitalize">
                            {(
                              cdDispatch.codPaymentLocation || "Delivery"
                            ).toLowerCase()}
                          </span>
                          .
                        </p>
                      </div>
                    )}
                    {cdDispatch.codAmount != null && (
                      <div>
                        <p className="text-xs text-muted-foreground">
                          COD Amount
                        </p>
                        <p>
                          ${Number(cdDispatch.codAmount).toFixed(2)}{" "}
                          {cdDispatch.codAmountCurrency}
                        </p>
                      </div>
                    )}
                    {cdDispatch.balanceAmount != null && (
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Balance Due
                        </p>
                        <p>
                          ${Number(cdDispatch.balanceAmount).toFixed(2)}{" "}
                          {cdDispatch.balanceAmountCurrency}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-card border-white/5">
                  <CardHeader className="py-2 px-4 border-b border-white/5">
                    <CardTitle className="text-xs font-semibold flex items-center gap-1.5">
                      <Calendar className="h-3 w-3 text-accent" />
                      Dispatch Dates
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2 px-4 py-3 text-sm">
                    {cdVehicle?.shipperRequestedPickupDate && (
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Requested Pickup
                        </p>
                        <p>
                          {new Date(
                            cdVehicle.shipperRequestedPickupDate,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {cdVehicle?.shipperRequestedDropOffDate && (
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Requested Dropoff
                        </p>
                        <p>
                          {new Date(
                            cdVehicle.shipperRequestedDropOffDate,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {cdVehicle?.estimatedPickupDate && (
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Est. Pickup
                        </p>
                        <p className="text-blue-400">
                          {new Date(
                            cdVehicle.estimatedPickupDate,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {cdVehicle?.estimatedDropOffDate && (
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Est. Dropoff
                        </p>
                        <p className="text-blue-400">
                          {new Date(
                            cdVehicle.estimatedDropOffDate,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {cdVehicle?.actualPickupDate && (
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Actual Pickup
                        </p>
                        <p>
                          {new Date(
                            cdVehicle.actualPickupDate,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {cdVehicle?.actualDropOffDate && (
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Actual Delivery
                        </p>
                        <p>
                          {new Date(
                            cdVehicle.actualDropOffDate,
                          ).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {cdVehicle?.pickUpDateType && (
                      <div>
                        <p className="text-xs text-muted-foreground">
                          Pickup Date Type
                        </p>
                        <p className="capitalize">
                          {cdVehicle.pickUpDateType
                            .replace(/_/g, " ")
                            .toLowerCase()}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* â"€â"€ Complete Timeline â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
          {tlEvents.length > 0 && (
            <Card className="bg-card border-white/5">
              <CardHeader className="py-2 px-4 border-b border-white/5">
                <CardTitle className="font-outfit text-sm font-semibold flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-accent" />
                  Complete Timeline
                </CardTitle>
              </CardHeader>
              <CardContent className="px-4 py-3">
                <div className="relative">
                  <div className="absolute left-[5px] top-1 bottom-1 w-px bg-white/10" />
                  <div className="space-y-2">
                    {tlEvents.map((ev, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div
                          className="h-3 w-3 rounded-full border-2 bg-background flex-shrink-0"
                          style={{ borderColor: ev.color }}
                        />
                        <div className="flex-1 min-w-0 flex items-center justify-between gap-2">
                          <p className="text-xs font-medium">{ev.label}</p>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            {ev.detail && (
                              <span className="text-xs text-muted-foreground font-mono">
                                {ev.detail}
                              </span>
                            )}
                            <time className="text-xs text-muted-foreground whitespace-nowrap">
                              {ev.ts.toLocaleString()}
                            </time>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Dispatch Offers */}
          <Card className="bg-card border-white/5">
            <CardHeader className="py-2 px-4 border-b border-white/5">
              <CardTitle className="font-outfit text-sm font-semibold">
                Dispatch Offers ({offers?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="px-4 py-3">
              {!offers || offers.length === 0 ? (
                <p className="text-muted-foreground text-xs text-center py-4">
                  No offers yet
                </p>
              ) : (
                <div className="space-y-3">
                  {offers.map((offer) => (
                    <div
                      key={offer.id}
                      className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                    >
                      <div>
                        <p className="font-medium">
                          {offer.carrier_id || "Unknown Carrier"}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {offer.channel} · Score:{" "}
                          {offer.score?.toFixed(0) || "-"}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="font-semibold">
                          ${offer.offer_price?.toFixed(2)}
                        </span>
                        <Badge
                          variant={
                            offer.status === "accepted" ? "default" : "outline"
                          }
                        >
                          {offer.status}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* â"€â"€ History Side Panel â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€â"€ */}
      <Sheet open={historyOpen} onOpenChange={setHistoryOpen}>
        <SheetContent
          side="right"
          className="w-[480px] sm:max-w-[480px] flex flex-col bg-background border-border"
        >
          <SheetHeader className="pb-4 border-b border-border">
            <SheetTitle className="font-outfit text-lg">
              {load.id} History
            </SheetTitle>
            <p className="text-xs text-muted-foreground">
              Latest Update:{" "}
              {new Date(load.updated_at).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </SheetHeader>
          <ScrollArea className="flex-1 mt-4">
            {historyItems.length === 0 ? (
              <p className="text-center text-muted-foreground text-sm py-10">
                No history available
              </p>
            ) : (
              <div className="space-y-3 pr-2">
                {historyItems.map((ev, i) => (
                  <HistoryEventCard key={i} ev={ev} />
                ))}
              </div>
            )}
          </ScrollArea>
        </SheetContent>
      </Sheet>

      {/* Assign Carrier Dialog */}
      <Dialog open={assignDialog} onOpenChange={setAssignDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Carrier via Central Dispatch</DialogTitle>
            <DialogDescription>
              Enter the Central Dispatch carrier UUID (customerId). This will
              create a fulfillment dispatch on Central Dispatch and mark the
              load as dispatched.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            {load.central_dispatch_listing_id && (
              <div className="rounded-md bg-muted/30 p-3 text-xs space-y-1">
                <p className="text-muted-foreground">CD Listing ID</p>
                <p className="font-mono">{load.central_dispatch_listing_id}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="carrier-select">Carrier</Label>
              {carriersLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading carriers...
                </div>
              ) : (
                <Select value={carrierId} onValueChange={setCarrierId}>
                  <SelectTrigger
                    id="carrier-select"
                    className="bg-transparent border-border"
                  >
                    <SelectValue placeholder="Select a carrier..." />
                  </SelectTrigger>
                  <SelectContent>
                    {activeCarriers.length === 0 ? (
                      <SelectItem value="__none__" disabled>
                        No active carriers found
                      </SelectItem>
                    ) : (
                      activeCarriers.map((c) => (
                        <SelectItem
                          key={c.id}
                          value={c.customer_id || c.id}
                          disabled={!c.customer_id}
                        >
                          {c.company_name}
                          {c.customer_id ? (
                            <span className="ml-2 text-xs text-muted-foreground font-mono">
                              {c.customer_id.slice(0, 8)}...
                            </span>
                          ) : (
                            <span className="ml-2 text-xs text-yellow-500">
                              (no CD ID)
                            </span>
                          )}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setAssignDialog(false);
                setCarrierId("");
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleAssignCarrier}
              disabled={actionLoading === "assign" || !carrierId.trim()}
              className="bg-accent text-accent-foreground"
            >
              {actionLoading === "assign" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Assigning...
                </>
              ) : (
                <>
                  <UserCheck className="h-4 w-4 mr-2" />
                  Assign Carrier
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dispatch / Remove Listing Confirmation Dialog */}
      {(() => {
        const isCarrierAssigned = !!(load?.cd_dispatch_id || load?.selected_carrier_id);
        const depositAmt = load?.transport_type === "enclosed" ? 299 : 199;
        const isBusy = actionLoading === "cancel-dispatch" || actionLoading === "cancel-refund";
        const carrierName = carrier?.company_name || load?.cd_carrier_name || null;
        const carrierPhone = carrier?.phone || load?.cd_carrier_phone || null;

        const CD_CANCEL_REASONS = [
          "Cannot Contact Pick Up Location",
          "Carrier Unresponsive/Late",
          "Client Hold/Key Hold",
          "Customer Canceled",
          "Date Change/Cannot Meet Dates",
          "Extreme Weather",
          "Fees Due",
          "Location Change",
          "Personal Reasons",
          "Price Changed/Low Price",
          "Redeemed/Dealer Purchase",
          "Release/Gate Pass Not Available",
          "Truck/Trailer Mechanical Issues",
          "Unexpected INOP",
          "Unit Damaged",
          "Unit Not Available",
          "Unit Not There",
        ];

        return (
          <Dialog
            open={cancelDispatchDialog}
            onOpenChange={(open) => {
              if (!open) { setCancelReason(""); setCancelNotes(""); }
              setCancelDispatchDialog(open);
            }}
          >
            <DialogContent className="bg-card border-white/10 max-w-lg flex flex-col max-h-[90vh]">
              <DialogHeader className="shrink-0">
                <DialogTitle
                  className={`flex items-center gap-2 ${isCarrierAssigned ? "text-red-500" : "text-orange-500"}`}
                >
                  <XCircle className="h-5 w-5" />
                  {isCarrierAssigned ? "Cancel Dispatch" : "Remove from Central Dispatch"}
                </DialogTitle>
              </DialogHeader>

              <div className="overflow-y-auto flex-1 space-y-4 pr-1">
              {/* Load summary row */}
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm border border-white/10 rounded-md px-4 py-3 bg-muted/20">
                <div>
                  <span className="text-muted-foreground text-xs">Pick up</span>
                  <p className="font-medium">{load?.pickup_city ? `${load.pickup_city}, ${load?.pickup_state} ${load?.pickup_zip}` : load?.pickup_zip || "—"}</p>
                </div>
                <div className="text-right">
                  <span className="text-muted-foreground text-xs">Load ID</span>
                  <p className="font-mono font-medium text-xs">{load?.cd_load_id || load?.id}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs">Delivery</span>
                  <p className="font-medium">{load?.delivery_city ? `${load.delivery_city}, ${load?.delivery_state} ${load?.delivery_zip}` : load?.delivery_zip || "—"}</p>
                </div>
                <div className="text-right">
                  <span className="text-muted-foreground text-xs">Carrier</span>
                  <p className="font-medium">{carrierName || "—"}</p>
                </div>
                <div>
                  <span className="text-muted-foreground text-xs"># of vehicles</span>
                  <p className="font-medium">1</p>
                </div>
              </div>

              <div className="space-y-4 py-1">
                {/* Reason for cancellation dropdown */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">
                    Reason for cancellation
                  </Label>
                  <Select value={cancelReason} onValueChange={setCancelReason}>
                    <SelectTrigger className="bg-background border-white/10">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {CD_CANCEL_REASONS.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Additional notes */}
                <div className="space-y-1.5">
                  <Label className="text-sm font-medium">Additional notes</Label>
                  <Textarea
                    placeholder="Enter cancellation notes"
                    value={cancelNotes}
                    maxLength={300}
                    rows={4}
                    onChange={(e) => setCancelNotes(e.target.value)}
                    className="bg-background border-white/10 resize-none"
                  />
                  <p className="text-xs text-muted-foreground text-right">
                    {300 - cancelNotes.length} characters remaining
                  </p>
                </div>
              </div>

              {/* Warning text (carrier case) */}
              {isCarrierAssigned && carrierName && (
                <div className="text-sm text-muted-foreground">
                  You have chosen to cancel your dispatch for{" "}
                  <span className="font-semibold text-foreground">{carrierName}</span>{" "}
                  to move your vehicle. Once you have cancelled your dispatch, a notification
                  email will be sent to the carrier and a copy will be retained.
                </div>
              )}

              {/* Carrier contact card */}
              {isCarrierAssigned && (carrierPhone || carrierName) && (
                <div className="rounded-md border border-white/10 bg-muted/20 px-4 py-3 text-sm space-y-1.5">
                  <p className="font-semibold text-xs text-muted-foreground uppercase tracking-wide">
                    We also recommend you contact the carrier directly at
                  </p>
                  {carrierName && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Carrier name</span>
                      <span className="font-medium">{carrierName}</span>
                    </div>
                  )}
                  {load?.cd_driver_name && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Contact</span>
                      <span className="font-medium">{load.cd_driver_name}</span>
                    </div>
                  )}
                  {carrierPhone && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Phone</span>
                      <a
                        href={`tel:${carrierPhone}`}
                        className="text-blue-400 hover:underline font-medium"
                      >
                        {carrierPhone}
                      </a>
                    </div>
                  )}
                </div>
              )}

              <p className="text-sm text-muted-foreground text-center">
                Are you sure you want to {isCarrierAssigned ? "cancel the dispatch" : "remove this listing"}?
              </p>
              </div>{/* end scrollable area */}

              <DialogFooter className="gap-2 flex-wrap shrink-0 border-t border-white/10 pt-4">
                <Button
                  variant="outline"
                  onClick={() => { setCancelDispatchDialog(false); setCancelReason(""); setCancelNotes(""); }}
                  className="border-white/10"
                  disabled={isBusy}
                >
                  {isCarrierAssigned ? "Keep Dispatch" : "Cancel"}
                </Button>

                {isCarrierAssigned ? (
                  <>
                    {/* Case 1: Cancel only (no refund) */}
                    <Button
                      variant="outline"
                      className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      onClick={() => handleCancelDispatch(false)}
                      disabled={isBusy}
                    >
                      {actionLoading === "cancel-dispatch" ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      Cancel Only
                    </Button>
                    {/* Case 1: Cancel + refund deposit */}
                    <Button
                      className="bg-red-600 hover:bg-red-700 text-white"
                      onClick={() => handleCancelDispatch(true)}
                      disabled={isBusy || !load?.deposit_paid_at || !!load?.refunded_at}
                    >
                      {actionLoading === "cancel-refund" ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <XCircle className="h-4 w-4 mr-2" />
                      )}
                      Cancel + Refund ${depositAmt}
                    </Button>
                  </>
                ) : (
                  /* Case 2: Delete listing + refund */
                  <Button
                    className="bg-orange-600 hover:bg-orange-700 text-white"
                    onClick={() => handleCancelDispatch(true)}
                    disabled={isBusy || !load?.deposit_paid_at || !!load?.refunded_at}
                  >
                    {actionLoading === "cancel-refund" ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <XCircle className="h-4 w-4 mr-2" />
                    )}
                    Delete Listing + Refund ${depositAmt}
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
        );
      })()}
    </>
  );
}


